<!-- src/views/Ops/RmDashboard.vue -->
<template>
  <div class="page">
    <div class="top">
      <div>
        <h2 class="title">RM Dashboard</h2>
        <div class="sub">Stock overview + recent inventory transactions.</div>
      </div>

      <div class="actions">
        <input
          class="input search"
          v-model="q"
          placeholder="Search SKU / Material…"
          @keyup.enter="loadStock"
        />
        <button class="btn" @click="refreshAll" :disabled="loadingAny">
          <span v-if="!loadingAny"><i class="fa fa-refresh"></i> Refresh</span>
          <span v-else>Loading…</span>
        </button>
      </div>
    </div>

    <div class="grid">
      <div class="card">
        <div class="card-title">Stock Items</div>
        <div class="big">{{ stock.length }}</div>
        <div class="muted">Matched rows in stock table</div>
      </div>

      <div class="card">
        <div class="card-title">Quick Actions</div>
        <div class="muted">
          Use <b>Incoming</b> for bill entry / restock.<br />
          Use <b>Outgoing</b> for issue / adjust.
        </div>
        <div class="btnrow">
          <button class="btn light" @click="goIncoming">Incoming</button>
          <button class="btn" @click="goOutgoing">Outgoing</button>
        </div>
      </div>

      <div class="card">
        <div class="card-title">Transactions</div>
        <div class="big">{{ recentTxns.length }}</div>
        <div class="muted">Recent ledger rows (latest first)</div>
      </div>
    </div>

    <div v-if="error" class="err card">
      <i class="fa fa-exclamation-triangle"></i> {{ error }}
    </div>

    <!-- STOCK -->
    <div class="card table-card">
      <div class="card-head">
        <div class="card-title">Stock</div>
        <div class="muted">
          {{ loadingStock ? 'Loading…' : 'Updated live from inventory_stock' }}
        </div>
      </div>

      <div v-if="!stock.length && !loadingStock" class="empty">
        No stock rows found.
      </div>

      <div class="tableWrap">
        <table class="table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Material</th>
              <th>Unit</th>
              <th style="text-align:right;">On Hand</th>
              <th style="text-align:right;">Min</th>
              <th>Updated</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="r in stock" :key="r.sku">
              <td class="mono">{{ safe(r.sku) }}</td>
              <td>{{ safe(r.name) }}</td>
              <td class="mono">{{ safe(r.unit) }}</td>
              <td class="mono" style="text-align:right;">
                {{ fmtNum(r.qtyOnHand) }}
              </td>
              <td class="mono" style="text-align:right;">
                {{ fmtNum(r.minQty) }}
              </td>
              <td class="mono">{{ fmtDate(r.updatedAt) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="foot">
        <button class="btn" @click="loadStock" :disabled="loadingStock">
          Refresh Stock
        </button>
      </div>
    </div>

    <!-- TXNS -->
    <div class="card table-card" style="margin-top: 14px;">
      <div class="card-head">
        <div class="card-title">Recent Transactions</div>
        <div class="muted">
          {{ loadingTxns ? 'Loading…' : 'From inventory_txn' }}
        </div>
      </div>

      <div class="tableWrap">
        <table class="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Dir</th>
              <th>SKU</th>
              <th style="text-align:right;">Qty</th>
              <th>Unit</th>
              <th>Ref</th>
              <th>Note</th>
              <th>By</th>
              <th>At</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="t in recentTxns" :key="t.id">
              <td class="mono">{{ safe(t.id) }}</td>
              <td>
                <span class="chip" :class="dirClass(t.direction)">
                  {{ safe(t.direction) }}
                </span>
              </td>
              <td class="mono">{{ safe(t.sku) }}</td>
              <td class="mono" style="text-align:right;">
                {{ fmtNum(t.qty) }}
              </td>
              <td class="mono">{{ safe(t.unit) }}</td>
              <td class="mono">{{ fmtRef(t.refType, t.refId) }}</td>
              <td class="note">{{ t.note || '-' }}</td>
              <td class="mono">{{ safe(t.createdBy) }}</td>
              <td class="mono">{{ fmtDate(t.createdAt) }}</td>
            </tr>

            <tr v-if="!recentTxns.length && !loadingTxns">
              <td colspan="9" class="emptyRow">No transactions found.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="foot">
        <button class="btn" @click="loadTxns" :disabled="loadingTxns">
          Refresh Txns
        </button>
      </div>
    </div>

    <!-- =========================
         MONTHLY TOTALS + DOWNLOAD
         ========================= -->
    <div class="card table-card" style="margin-top: 14px;">
      <div class="card-head">
        <div class="card-title">Monthly History</div>

        <div class="history-controls">
          <label class="muted" style="margin-right:8px;">Month</label>
          <input class="input month" type="month" v-model="month" />
          <button class="btn" @click="downloadMonthlyTxnsCsv" :disabled="!monthlyTxns.length">
            Download Txns CSV
          </button>
          <button class="btn" @click="downloadStockCsv" :disabled="!stock.length">
            Download Stock CSV
          </button>
        </div>
      </div>

      <div class="history-grid">
        <div class="mini">
          <div class="mini-title">IN (month)</div>
          <div class="mini-big">{{ fmtNum(monthTotals.inQty) }}</div>
          <div class="muted">Sum of IN qty (all SKUs)</div>
        </div>

        <div class="mini">
          <div class="mini-title">OUT (month)</div>
          <div class="mini-big">{{ fmtNum(monthTotals.outQty) }}</div>
          <div class="muted">Sum of OUT qty (all SKUs)</div>
        </div>

        <div class="mini">
          <div class="mini-title">ADJUST (month)</div>
          <div class="mini-big">{{ fmtNum(monthTotals.adjustQty) }}</div>
          <div class="muted">Sum of ADJUST qty (can be ±)</div>
        </div>

        <div class="mini">
          <div class="mini-title">Rows (month)</div>
          <div class="mini-big">{{ monthlyTxns.length }}</div>
          <div class="muted">Transactions that fall in selected month</div>
        </div>
      </div>

      <div class="muted hint">
        Note: Monthly totals are computed from the transactions loaded into this page. If your month has
        more than the fetch limit, increase the limit in <span class="mono">loadTxns()</span>.
      </div>

      <div class="tableWrap">
        <table class="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Dir</th>
              <th>SKU</th>
              <th style="text-align:right;">Qty</th>
              <th>Unit</th>
              <th>Ref</th>
              <th>Note</th>
              <th>By</th>
              <th>At</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="t in monthlyTxns" :key="'m_' + t.id">
              <td class="mono">{{ safe(t.id) }}</td>
              <td>
                <span class="chip" :class="dirClass(t.direction)">
                  {{ safe(t.direction) }}
                </span>
              </td>
              <td class="mono">{{ safe(t.sku) }}</td>
              <td class="mono" style="text-align:right;">
                {{ fmtNum(t.qty) }}
              </td>
              <td class="mono">{{ safe(t.unit) }}</td>
              <td class="mono">{{ fmtRef(t.refType, t.refId) }}</td>
              <td class="note">{{ t.note || '-' }}</td>
              <td class="mono">{{ safe(t.createdBy) }}</td>
              <td class="mono">{{ fmtDate(t.createdAt) }}</td>
            </tr>

            <tr v-if="!monthlyTxns.length">
              <td colspan="9" class="emptyRow">No transactions in selected month.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="foot">
        <button class="btn" @click="downloadMonthlyTxnsCsv" :disabled="!monthlyTxns.length">
          Download Txns CSV
        </button>
        <button class="btn" @click="downloadStockCsv" :disabled="!stock.length">
          Download Stock CSV
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RmDashboard',
  data: function () {
    return {
      q: '',
      // HTML month input expects "YYYY-MM"
      month: this._currentMonthKey()
    }
  },

  computed: {
    stock () {
      return this.$store.getters['inventory/stock']
    },
    recentTxns () {
      return this.$store.getters['inventory/recentTxns']
    },

    loadingStock () {
      return !!(this.$store.state.inventory && this.$store.state.inventory.loadingStock)
    },
    loadingTxns () {
      return !!(this.$store.state.inventory && this.$store.state.inventory.loadingTxns)
    },
    loadingMasters () {
      return !!(this.$store.state.inventory && this.$store.state.inventory.loadingMasters)
    },
    loadingAny () {
      return this.loadingStock || this.loadingTxns || this.loadingMasters
    },

    error () {
      return (this.$store.state.inventory && this.$store.state.inventory.lastError) || ''
    },

    monthlyTxns () {
      var key = String(this.month || '').trim()
      if (!key) return []
      var list = Array.isArray(this.recentTxns) ? this.recentTxns : []
      // createdAt from backend is iso-ish; compare prefix YYYY-MM
      return list.filter(function (t) {
        var d = (t && t.createdAt) ? String(t.createdAt) : ''
        return d.indexOf(key) === 0
      })
    },

    monthTotals () {
      var inQty = 0
      var outQty = 0
      var adjustQty = 0

      for (var i = 0; i < this.monthlyTxns.length; i++) {
        var t = this.monthlyTxns[i] || {}
        var dir = String(t.direction || '').toUpperCase()
        var qty = Number(t.qty)
        if (!isFinite(qty)) qty = 0

        if (dir === 'IN') inQty += qty
        else if (dir === 'OUT') outQty += qty
        else adjustQty += qty
      }

      return {
        inQty: inQty,
        outQty: outQty,
        adjustQty: adjustQty
      }
    }
  },

  created: function () {
    this.refreshAll()
  },

  methods: {
    _currentMonthKey: function () {
      try {
        var d = new Date()
        var y = d.getFullYear()
        var m = String(d.getMonth() + 1).padStart(2, '0')
        return y + '-' + m
      } catch (e) {
        return ''
      }
    },

    safe: function (v) {
      if (v === null || v === undefined || v === '') return '-'
      return String(v)
    },

    fmtNum: function (v) {
      if (v === null || v === undefined || v === '') return '-'
      var n = Number(v)
      if (!isFinite(n)) return String(v)
      var s = n.toFixed(2)
      if (s.indexOf('.') !== -1) s = s.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1')
      return s
    },

    fmtDate: function (iso) {
      if (!iso) return '-'
      try {
        return new Date(iso).toLocaleString()
      } catch (e) {
        return String(iso)
      }
    },

    fmtRef: function (refType, refId) {
      refType = (refType || 'NONE').toUpperCase()
      if (refType === 'ORDER' && refId) return 'ORDER #' + refId
      return '—'
    },

    dirClass: function (d) {
      d = String(d || '').toUpperCase()
      if (d === 'IN') return 'chip-in'
      if (d === 'OUT') return 'chip-out'
      return 'chip-adj'
    },

    async refreshAll () {
      await this.$store.dispatch('inventory/loadMasters')
      await this.loadStock()
      await this.loadTxns()
    },

    async loadStock () {
      await this.$store.dispatch('inventory/loadStock', { q: this.q })
    },

    async loadTxns () {
      // Increase limit so month totals are more likely accurate.
      // If you have heavy volume, consider adding a backend month-filter endpoint.
      await this.$store.dispatch('inventory/loadRecentTxns', { limit: 800 })
    },

    goIncoming: function () {
      this.$router.push({ name: 'ops-rm-incoming' })
    },

    goOutgoing: function () {
      this.$router.push({ name: 'ops-rm-outgoing' })
    },

    // -------------------------
    // CSV downloads (client-side)
    // -------------------------
    _csvEscape: function (v) {
      if (v === null || v === undefined) return ''
      var s = String(v)
      // Escape quotes, wrap if needed
      if (s.indexOf('"') !== -1) s = s.replace(/"/g, '""')
      if (/[",\n\r]/.test(s)) s = '"' + s + '"'
      return s
    },

    _downloadTextFile: function (filename, content, mime) {
      try {
        var blob = new Blob([content], { type: mime || 'text/plain;charset=utf-8' })
        var url = URL.createObjectURL(blob)
        var a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (e) {
        // fallback: do nothing
      }
    },

    downloadStockCsv: function () {
      var rows = Array.isArray(this.stock) ? this.stock : []
      var head = ['sku', 'name', 'unit', 'qtyOnHand', 'minQty', 'updatedAt']
      var lines = [head.join(',')]

      for (var i = 0; i < rows.length; i++) {
        var r = rows[i] || {}
        lines.push([
          this._csvEscape(r.sku),
          this._csvEscape(r.name),
          this._csvEscape(r.unit),
          this._csvEscape(r.qtyOnHand),
          this._csvEscape(r.minQty),
          this._csvEscape(r.updatedAt)
        ].join(','))
      }

      var fname = 'rm_stock_' + this._currentMonthKey() + '.csv'
      this._downloadTextFile(fname, lines.join('\n'), 'text/csv;charset=utf-8')
    },

    downloadMonthlyTxnsCsv: function () {
      var key = String(this.month || '').trim() || this._currentMonthKey()
      var rows = Array.isArray(this.monthlyTxns) ? this.monthlyTxns : []
      var head = ['id', 'direction', 'sku', 'qty', 'unit', 'refType', 'refId', 'note', 'createdBy', 'createdAt']
      var lines = [head.join(',')]

      for (var i = 0; i < rows.length; i++) {
        var t = rows[i] || {}
        lines.push([
          this._csvEscape(t.id),
          this._csvEscape(t.direction),
          this._csvEscape(t.sku),
          this._csvEscape(t.qty),
          this._csvEscape(t.unit),
          this._csvEscape(t.refType),
          this._csvEscape(t.refId),
          this._csvEscape(t.note),
          this._csvEscape(t.createdBy),
          this._csvEscape(t.createdAt)
        ].join(','))
      }

      // Add totals footer (optional)
      lines.push('')
      lines.push('Totals,,,,,,,,,')
      lines.push('IN,' + this._csvEscape(this.monthTotals.inQty))
      lines.push('OUT,' + this._csvEscape(this.monthTotals.outQty))
      lines.push('ADJUST,' + this._csvEscape(this.monthTotals.adjustQty))

      var fname = 'rm_txns_' + key + '.csv'
      this._downloadTextFile(fname, lines.join('\n'), 'text/csv;charset=utf-8')
    }
  }
}
</script>

<style scoped>
.page { display: flex; flex-direction: column; gap: 14px; }

.top {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.title { margin: 0; font-size: 22px; font-weight: 900; color: #0f172a; }
.sub { color: #64748b; font-size: 13px; margin-top: 4px; }

.actions { display:flex; gap:10px; align-items:center; }
.search { min-width: 260px; }

.grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
@media (max-width: 900px) {
  .grid { grid-template-columns: 1fr; }
  .actions { width: 100%; }
  .search { flex: 1; min-width: 0; }
}

.card {
  background: #fff;
  border: 1px solid #e6e9f2;
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 10px 30px rgba(15,23,42,.04);
}

.card-title { font-weight: 800; color: #0f172a; }
.big { font-size: 34px; font-weight: 900; margin-top: 6px; }
.muted { color: #64748b; font-size: 13px; margin-top: 6px; }

.btnrow { display:flex; gap:10px; margin-top: 10px; flex-wrap: wrap; }

.table-card { padding: 0; overflow: hidden; }
.card-head {
  padding: 14px;
  border-bottom: 1px solid #eef1f7;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: baseline;
  flex-wrap: wrap;
}

.tableWrap { overflow: auto; }
.table { width: 100%; border-collapse: collapse; font-size: 14px; }
.table th, .table td { border-top: 1px solid #eef1f7; padding: 10px 10px; vertical-align: top; }
.table thead th { background: #fafbff; border-top: none; font-weight: 900; color: #0f172a; }

.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
.note { max-width: 320px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.foot {
  padding: 12px 14px;
  border-top: 1px solid #eef1f7;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.empty { padding: 16px; color: #64748b; }
.emptyRow { padding: 16px; color: #64748b; text-align: center; }

.chip {
  font-size: 12px; font-weight: 900;
  padding: 4px 8px; border-radius: 999px;
  background: #eef2ff; color: #3730a3;
  border: 1px solid rgba(55,48,163,0.15);
}
.chip-in  { background: #ecfdf5; color: #065f46; border-color: rgba(6,95,70,0.18); }
.chip-out { background: #fff7ed; color: #7c2d12; border-color: rgba(124,45,18,0.18); }
.chip-adj { background: #eff6ff; color: #1d4ed8; border-color: rgba(29,78,216,0.18); }

.btn {
  border: 1px solid #d7dceb;
  background: #fff;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 800;
}
.btn.light { background: #0f172a; color: #fff; border-color: #0f172a; }

.err {
  padding: 12px 14px;
  color: #b91c1c;
}

/* Monthly history styles */
.history-controls {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.month { height: 40px; }

.history-grid{
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  padding: 14px;
}
@media (max-width: 900px) {
  .history-grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 520px) {
  .history-grid { grid-template-columns: 1fr; }
}

.mini{
  background: #fff;
  border: 1px solid #eef1f7;
  border-radius: 14px;
  padding: 12px;
}
.mini-title{ font-weight: 900; color: #0f172a; font-size: 13px; }
.mini-big{ font-size: 22px; font-weight: 900; margin-top: 6px; }

.hint{
  padding: 0 14px 14px 14px;
}
</style>
